// ApprovePicker — RRA v2.2 (WEL-180): "Which animal(s) does this cover?"
//
// Approving an animal request on a bound pre-lease application picks the animal(s) from the
// household's roster (AppFolio animals + tenant-intake animals), or types one that is not on file
// yet. The picked animals are listed at $0 on the lease; the household's others stay chargeable.
// The same picker backs "Change animals" on an approved request.
//
// It is an inline panel, not a second dialog: the case drawer is already a focus-trapped modal, and
// a modal on a modal would fight it for focus and Escape (the deny / more-info / withdraw forms on
// the same card are inline for the same reason).
//
//   cd apps/web && npx tsx --test src/components/rra/ApprovePicker.test.ts
//
// Names, kinds and breeds only — a roster row's machine id travels with the pick and is never shown.

import { useId, useState } from 'react';
import {
  PICKER_HEADING,
  PICKER_NONE_ON_FILE,
  PICKER_NOT_ON_FILE,
  PICKER_PENDING_IN_BUILDER,
  PICKER_UNAVAILABLE,
  initialPickerState,
  pickerHelp,
  pickerAnimals,
  pickerChoices,
  pickerReady,
  rosterRowTag,
  rosterRowWords,
  toggleSelected,
  type PickerState,
  type RosterLoad,
} from '@/lib/rraApprovePicker';
import type { RraApproveAnimal, RraCaseView, RraItemView } from '@/lib/rraStaffClient';

export type ApprovePickerMode = 'approve' | 'change';

export function ApprovePicker(props: {
  /** The request being approved (or whose animals are being changed). */
  item: Pick<RraItemView, 'itemId' | 'approvedAnimals'>;
  mode: ApprovePickerMode;
  /** The case's kind — a tenancy approval words the help for the household animal form. */
  caseKind?: RraCaseView['kind'];
  roster: RosterLoad;
  busy: boolean;
  onConfirm: (animals: RraApproveAnimal[]) => void;
  onCancel: () => void;
}): React.JSX.Element {
  if (props.roster.status === 'loading') {
    return (
      <div className="card" data-section="rra-approve-picker" role="status" style={{ marginTop: 'var(--space-3)' }}>
        <div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)' }}>Loading the household’s animals…</div>
      </div>
    );
  }
  return <PickerBody {...props} roster={props.roster} />;
}

function PickerBody(props: {
  item: Pick<RraItemView, 'itemId' | 'approvedAnimals'>;
  mode: ApprovePickerMode;
  caseKind?: RraCaseView['kind'];
  roster: Exclude<RosterLoad, { status: 'loading' }>;
  busy: boolean;
  onConfirm: (animals: RraApproveAnimal[]) => void;
  onCancel: () => void;
}): React.JSX.Element {
  const { item, mode, roster, busy } = props;
  const uid = useId();
  const choices = pickerChoices(roster.status === 'ready' ? roster.animals : [], item);
  const [state, setState] = useState<PickerState>(() => initialPickerState(choices, item));
  const ready = pickerReady(choices, state);
  const nothingToPick = choices.length === 0;
  const nameId = `${uid}-typed-name`;
  const typeId = `${uid}-typed-type`;

  return (
    <div
      className="card"
      data-section="rra-approve-picker"
      style={{ marginTop: 'var(--space-3)', background: 'var(--surface-2, transparent)' }}
    >
      <fieldset className="fieldset stack-sm" style={{ border: 0, padding: 0, marginInline: 0, minWidth: 0 }}>
        <legend style={{ fontWeight: 'var(--weight-medium)', padding: 0 }}>{PICKER_HEADING}</legend>
        <div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)' }}>{pickerHelp(props.caseKind ?? 'pre_lease')}</div>

        {roster.status === 'unavailable' ? (
          <div className="alert" data-tone="warning" role="status">
            <div className="alert-body" style={{ fontSize: 'var(--text-sm)' }}>{PICKER_UNAVAILABLE}</div>
          </div>
        ) : nothingToPick ? (
          <div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)' }}>{PICKER_NONE_ON_FILE}</div>
        ) : null}

        {choices.length > 0 ? (
          <ul className="stack-sm" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {choices.map((a) => {
              const tag = rosterRowTag(a, item.itemId);
              return (
                <li key={a.id}>
                  <label className="choice" style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={state.selected.includes(a.id)}
                      disabled={busy}
                      onChange={() => setState((s) => toggleSelected(s, a.id))}
                      style={{ flex: 'none', marginTop: '0.2em' }}
                    />
                    <span>{rosterRowWords(a)}</span>
                    {tag ? (
                      <span className="badge" data-size="sm" data-tone="success">{tag}</span>
                    ) : null}
                    {a.pendingInBuilder === true ? (
                      <span className="badge" data-size="sm" data-tone="info">{PICKER_PENDING_IN_BUILDER}</span>
                    ) : null}
                  </label>
                </li>
              );
            })}
          </ul>
        ) : null}

        {/* The animal that is not on file yet. With nothing on the roster this row is the only way forward. */}
        {!nothingToPick ? (
          <label className="choice" style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={state.typedOn}
              disabled={busy}
              onChange={(e) => setState((s) => ({ ...s, typedOn: e.target.checked }))}
              style={{ flex: 'none', marginTop: '0.2em' }}
            />
            <span>{PICKER_NOT_ON_FILE}</span>
          </label>
        ) : null}
        {state.typedOn ? (
          <div className="cluster" style={{ gap: 'var(--space-3)', alignItems: 'flex-end', marginLeft: nothingToPick ? 0 : 'var(--space-6)' }}>
            <div className="field" style={{ minWidth: 200 }}>
              <label className="label" htmlFor={nameId}>Animal’s name</label>
              <input
                id={nameId}
                className="input"
                maxLength={120}
                value={state.typedName}
                disabled={busy}
                onChange={(e) => setState((s) => ({ ...s, typedName: e.target.value }))}
              />
            </div>
            <div className="field">
              <label className="label" htmlFor={typeId}>Type</label>
              <select
                id={typeId}
                className="input"
                value={state.typedType}
                disabled={busy}
                onChange={(e) => setState((s) => ({ ...s, typedType: e.target.value as PickerState['typedType'] }))}
              >
                <option value="dog">Dog</option>
                <option value="cat">Cat</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        ) : null}
      </fieldset>

      <div className="cluster" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
        <button
          type="button"
          className="btn"
          data-variant="primary"
          data-size="sm"
          disabled={busy || !ready}
          onClick={() => props.onConfirm(pickerAnimals(choices, state))}
        >
          {mode === 'approve' ? (busy ? 'Approving…' : 'Approve') : busy ? 'Saving…' : 'Save animals'}
        </button>
        <button type="button" className="btn" data-variant="ghost" data-size="sm" disabled={busy} onClick={props.onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
